import React, { useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, Button, ToggleButton, ToggleButtonGroup,
  Table, TableHead, TableRow, TableCell, TableBody,
  TableSortLabel, CircularProgress, Alert, Paper, Divider, Tooltip,
} from '@mui/material';
import { Download, InfoOutlined } from '@mui/icons-material';
import { format, subWeeks, subMonths } from 'date-fns';
import api from '../../services/api';

const C = {
  blue:   '#005EB8',
  green:  '#009639',
  yellow: '#FFB81C',
  red:    '#DA291C',
  dark:   '#425563',
  mid:    '#768692',
  pale:   '#E8EDEE',
  white:  '#FFFFFF',
};

function rateFg(pct) {
  if (pct >= 90) return C.green;
  if (pct >= 70) return '#7B5800';
  return C.red;
}
function rateBg(pct) {
  if (pct >= 90) return '#E8F5E9';
  if (pct >= 70) return '#FFF8E1';
  return '#FFEBEE';
}

function presetDates(preset) {
  const today = new Date();
  const fmt   = d => format(d, 'yyyy-MM-dd');
  if (preset === '2w') return { start: fmt(subWeeks(today, 2)),  end: fmt(today) };
  if (preset === '4w') return { start: fmt(subWeeks(today, 4)),  end: fmt(today) };
  if (preset === '3m') return { start: fmt(subMonths(today, 3)), end: fmt(today) };
  return null;
}

function getCellStatus(byDate, date) {
  const d = byDate[date];
  if (!d || d.expected === 0) return 'none';
  if (d.submitted === d.expected) return 'complete';
  if (d.submitted > 0) return 'partial';
  return 'missing';
}

// ─── Pivot cell ───────────────────────────────────────────────────────────────

function PivotCell({ status, expected, submitted }) {
  if (status === 'none') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 36, height: 26, color: C.pale, fontSize: '0.75rem' }}>
        —
      </Box>
    );
  }
  const cfg = {
    complete: { bg: '#E8F5E9', color: C.green,  icon: '✓', tip: `All ${expected} check${expected !== 1 ? 's' : ''} submitted` },
    partial:  { bg: '#FFF8E1', color: '#7B5800', icon: '½', tip: `${submitted} of ${expected} submitted` },
    missing:  { bg: '#FFEBEE', color: C.red,     icon: '✗', tip: `${expected} check${expected !== 1 ? 's' : ''} not submitted` },
  };
  const { bg, color, icon, tip } = cfg[status];
  return (
    <Tooltip title={tip} placement="top" arrow>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 36, height: 26, bgcolor: bg, color, borderRadius: '4px',
        fontSize: '0.75rem', fontWeight: 700, cursor: 'default', mx: 'auto' }}>
        {icon}
      </Box>
    </Tooltip>
  );
}

// ─── Pivot table ──────────────────────────────────────────────────────────────

function PivotTable({ dates, pivot }) {
  if (!dates.length || !pivot.length) return null;

  const dateObjs = dates.map(d => new Date(d + 'T12:00:00'));

  // Group consecutive dates by week (Monday → label)
  const weekGroups = [];
  dates.forEach((d, i) => {
    const dt  = dateObjs[i];
    const dow = dt.getDay() || 7;
    const mon = new Date(dt);
    mon.setDate(dt.getDate() - (dow - 1));
    const wk = format(mon, 'dd MMM');
    if (!weekGroups.length || weekGroups[weekGroups.length - 1].label !== wk) {
      weekGroups.push({ label: wk, span: 0 });
    }
    weekGroups[weekGroups.length - 1].span++;
  });

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ borderCollapse: 'separate' }}>
        <TableHead>
          {/* Week row */}
          <TableRow>
            <TableCell sx={{
              position: 'sticky', left: 0, zIndex: 3, bgcolor: C.white,
              borderBottom: 'none', minWidth: 170, borderRight: `2px solid ${C.pale}`,
            }} />
            {weekGroups.map((wg, i) => (
              <TableCell key={i} colSpan={wg.span} align="center"
                sx={{ borderBottom: 'none', py: 0.5,
                  bgcolor: i % 2 === 0 ? '#EEF2F6' : '#E4EAF0',
                  fontSize: '0.63rem', fontWeight: 700, color: C.dark,
                  letterSpacing: 0.4, whiteSpace: 'nowrap' }}>
                W/C {wg.label}
              </TableCell>
            ))}
            <TableCell sx={{ borderBottom: 'none' }} />
          </TableRow>

          {/* Date row */}
          <TableRow sx={{ bgcolor: C.blue }}>
            <TableCell sx={{
              position: 'sticky', left: 0, zIndex: 3, bgcolor: C.blue,
              color: C.white, borderBottom: 'none', py: 0.75, minWidth: 170,
              fontSize: '0.68rem', fontWeight: 700, letterSpacing: 0.3,
              textTransform: 'uppercase', borderRight: `2px solid rgba(255,255,255,0.2)`,
            }}>
              Clinic
            </TableCell>
            {dateObjs.map((d, i) => (
              <TableCell key={i} align="center"
                sx={{ py: 0.75, borderBottom: 'none', px: 0.5, minWidth: 46 }}>
                <Box sx={{ color: C.white, fontSize: '0.6rem', fontWeight: 700,
                  lineHeight: 1.35, textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {format(d, 'EEE')}<br />{format(d, 'dd/MM')}
                </Box>
              </TableCell>
            ))}
            <TableCell align="right"
              sx={{ py: 0.75, borderBottom: 'none', pr: 1.5,
                color: C.white, fontSize: '0.62rem', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap' }}>
              Rate
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {pivot.map((row, ri) => {
            const rowBg = ri % 2 === 0 ? C.white : '#F7FAFC';
            return (
              <TableRow key={row.clinic_id} hover sx={{ backgroundColor: rowBg }}>
                <TableCell sx={{
                  position: 'sticky', left: 0, zIndex: 1, backgroundColor: rowBg,
                  fontWeight: 500, fontSize: '0.78rem', py: 0.5,
                  borderRight: `2px solid ${C.pale}`, whiteSpace: 'nowrap',
                }}>
                  {row.clinic_name}
                </TableCell>
                {dates.map(date => {
                  const status = getCellStatus(row.by_date, date);
                  const d      = row.by_date[date] || { expected: 0, submitted: 0 };
                  return (
                    <TableCell key={date} align="center" sx={{ py: 0.5, px: 0.25 }}>
                      <PivotCell status={status} expected={d.expected} submitted={d.submitted} />
                    </TableCell>
                  );
                })}
                <TableCell align="right" sx={{ pr: 1.5, py: 0.5 }}>
                  <Box sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: 1,
                    fontSize: '0.75rem', fontWeight: 700,
                    color: rateFg(row.rate), bgcolor: rateBg(row.rate) }}>
                    {row.rate}%
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

// ─── Run chart ────────────────────────────────────────────────────────────────

const VB_W = 900, VB_H = 200;
const PAD  = { top: 28, right: 80, bottom: 52, left: 44 };
const CW   = VB_W - PAD.left - PAD.right;
const CH   = VB_H - PAD.top  - PAD.bottom;

function RunChart({ timeSeries }) {
  if (!timeSeries?.length) return null;
  const n    = timeSeries.length;
  const barW = Math.min(60, (CW / n) * 0.6);
  const gap  = (CW - n * barW) / (n + 1);
  const barX = i => PAD.left + gap + i * (barW + gap);
  const yAt  = v => PAD.top + CH - (v / 100) * CH;

  return (
    <Box>
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} width="100%" preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', overflow: 'visible' }}>

        {[0, 25, 50, 75, 100].map(v => (
          <g key={v}>
            <line x1={PAD.left} y1={yAt(v)} x2={PAD.left + CW} y2={yAt(v)}
              stroke={v === 0 ? C.dark : C.pale} strokeWidth={v === 0 ? 1 : 0.5} />
            <text x={PAD.left - 5} y={yAt(v) + 4} textAnchor="end" fontSize={9} fill={C.mid}>{v}%</text>
          </g>
        ))}

        <line x1={PAD.left} y1={yAt(90)} x2={PAD.left + CW} y2={yAt(90)}
          stroke={C.green} strokeWidth={1} strokeDasharray="4 3" opacity={0.7} />
        <text x={PAD.left + CW + 4} y={yAt(90) + 4} fontSize={8} fill={C.green} opacity={0.8}>90% target</text>

        {timeSeries.map((d, i) => {
          const x     = barX(i);
          const h     = (d.rate / 100) * CH;
          const color = rateFg(d.rate);
          const dt    = new Date(d.period + 'T12:00:00');
          const lbl   = format(dt, 'dd MMM');
          return (
            <g key={i}>
              <rect x={x} y={yAt(d.rate)} width={barW} height={h}
                fill={color} opacity={0.8} rx={2}>
                <title>W/C {lbl}: {d.rate}% ({d.submitted}/{d.expected})</title>
              </rect>
              {h > 14 && (
                <text x={x + barW / 2} y={yAt(d.rate) - 5}
                  textAnchor="middle" fontSize={9} fontWeight="600" fill={color}>
                  {d.rate}%
                </text>
              )}
              <text x={x + barW / 2} y={PAD.top + CH + 16}
                textAnchor="middle" fontSize={8.5} fill={C.mid}>{lbl}</text>
              <text x={x + barW / 2} y={PAD.top + CH + 27}
                textAnchor="middle" fontSize={7.5} fill={C.mid} opacity={0.6}>w/c</text>
            </g>
          );
        })}

        <rect x={PAD.left} y={PAD.top} width={CW} height={CH}
          fill="none" stroke={C.pale} strokeWidth={1} />
      </svg>

      <Box sx={{ display: 'flex', gap: 2.5, mt: 0.5, pl: 0.5, flexWrap: 'wrap' }}>
        {[[C.green, '≥90%'], [C.yellow, '70–89%'], [C.red, '<70%']].map(([c, l]) => (
          <Box key={l} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 14, height: 14, bgcolor: c, borderRadius: '2px', opacity: 0.8 }} />
            <Typography sx={{ fontSize: '0.65rem', color: C.dark }}>{l}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ─── Summary card ─────────────────────────────────────────────────────────────

function StatCard({ label, primary, secondary, accentColor }) {
  return (
    <Paper elevation={0} sx={{
      flex: 1, minWidth: 140, p: 2, borderRadius: 2,
      border: `1.5px solid ${accentColor}30`,
      background: `linear-gradient(135deg, ${accentColor}08 0%, ${C.white} 100%)`,
    }}>
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.8, color: C.mid, mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, lineHeight: 1, color: accentColor }}>
        {primary}
      </Typography>
      {secondary && (
        <Typography sx={{ fontSize: '0.7rem', color: C.mid, mt: 0.5 }}>{secondary}</Typography>
      )}
    </Paper>
  );
}

// ─── By-clinic table ──────────────────────────────────────────────────────────

const CLINIC_COLS = [
  { id: 'clinic_name',     label: 'Clinic',    align: 'left'  },
  { id: 'total_expected',  label: 'Expected',  align: 'right' },
  { id: 'total_submitted', label: 'Submitted', align: 'right' },
  { id: 'total_missing',   label: 'Missing',   align: 'right' },
  { id: 'rate',            label: 'Rate',      align: 'right' },
];

function ByClinicTable({ pivot }) {
  const [sortKey, setSortKey] = useState('rate');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = key => {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'clinic_name' ? 'asc' : 'asc'); }
  };

  const sorted = useMemo(() => [...pivot].sort((a, b) => {
    const va = a[sortKey] ?? '', vb = b[sortKey] ?? '';
    if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    return sortDir === 'asc' ? va - vb : vb - va;
  }), [pivot, sortKey, sortDir]);

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: C.blue }}>
            {CLINIC_COLS.map(col => (
              <TableCell key={col.id} align={col.align}
                sortDirection={sortKey === col.id ? sortDir : false}
                sx={{ py: 1, whiteSpace: 'nowrap', borderBottom: 'none' }}>
                <TableSortLabel
                  active={sortKey === col.id}
                  direction={sortKey === col.id ? sortDir : 'asc'}
                  onClick={() => handleSort(col.id)}
                  sx={{
                    color: `${C.white} !important`,
                    '& .MuiTableSortLabel-icon': { color: `${C.white} !important`, opacity: 0.65 },
                    fontSize: '0.68rem', fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
                  }}>
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row, i) => (
            <TableRow key={row.clinic_id} hover
              sx={{ backgroundColor: i % 2 === 0 ? C.white : '#F7FAFC' }}>
              <TableCell sx={{ fontWeight: 500, fontSize: '0.78rem' }}>{row.clinic_name}</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{row.total_expected}</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.78rem', color: C.green, fontWeight: 600 }}>
                {row.total_submitted}
              </TableCell>
              <TableCell align="right" sx={{
                fontSize: '0.78rem',
                color: row.total_missing > 0 ? C.red : C.mid,
                fontWeight: row.total_missing > 0 ? 600 : 400,
              }}>
                {row.total_missing}
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: 1,
                  fontSize: '0.78rem', fontWeight: 700,
                  color: rateFg(row.rate), bgcolor: rateBg(row.rate) }}>
                  {row.rate}%
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// ─── Timing breakdown ─────────────────────────────────────────────────────────

function TimingBreakdown({ timing, totalSubmitted }) {
  const items = [
    { key: 'same_day',    label: 'Same day',  color: C.green  },
    { key: 'next_day',    label: 'Next day',  color: '#4CAF50' },
    { key: '2_3_days',   label: '2–3 days', color: C.yellow },
    { key: 'over_3_days', label: '4+ days',   color: C.red    },
  ];
  return (
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
      {items.map(({ key, label, color }) => {
        const count = timing[key] || 0;
        const pct   = totalSubmitted > 0 ? +((count / totalSubmitted) * 100).toFixed(1) : 0;
        return (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25,
            border: `1px solid ${color}40`, borderRadius: 1.5, bgcolor: `${color}08`,
            flex: 1, minWidth: 120 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: '0.68rem', color: C.mid }}>{label}</Typography>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color, lineHeight: 1.2 }}>
                {count}
              </Typography>
              <Typography sx={{ fontSize: '0.63rem', color: C.mid }}>{pct}% of submitted</Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── CSV export ───────────────────────────────────────────────────────────────

function exportCSV(pivot, dates) {
  const STATUS_LABEL = { complete: 'Yes', partial: 'Partial', missing: 'No', none: 'N/A' };
  const header = ['Clinic', 'Expected', 'Submitted', 'Missing', 'Rate %', ...dates].join(',');
  const lines  = pivot.map(r => {
    const cells = dates.map(d => STATUS_LABEL[getCellStatus(r.by_date, d)]);
    return [r.clinic_name, r.total_expected, r.total_submitted, r.total_missing, r.rate, ...cells]
      .map(v => `"${v}"`).join(',');
  });
  const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = 'nursing-checks-report.csv'; a.click();
  URL.revokeObjectURL(url);
}

function triggerRawCSV(rows, startDate, endDate) {
  const RAW_HEADERS = [
    'Date', 'Day', 'Clinic', 'Session', 'Booking ID', 'Specialty',
    'Room Number', 'Room Name', 'Check Submitted', 'Submitted At',
    'Room Status', 'Used By Specialty', 'Clinic Delay', 'Delay Reason',
    'Delay Impacted Another Clinic', 'Additional Issues',
  ];
  const SESSION_LABEL = { am: 'AM', pm: 'PM', all_day: 'All Day' };
  const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = rows.map(r => [
    r.date, (r.day_of_week || '').trim(), r.clinic_name,
    SESSION_LABEL[r.session] || r.session,
    r.booking_id, r.specialty, r.room_number, r.room_name,
    r.check_submitted, r.submitted_at || '',
    r.room_status, r.used_by_specialty, r.clinic_delay,
    r.delay_reason, r.delay_impacted_another_clinic, r.additional_issues,
  ].map(esc).join(','));
  const blob = new Blob([[RAW_HEADERS.map(esc).join(','), ...lines].join('\n')], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = `nursing-checks-raw-${startDate}-to-${endDate}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function NursingChecksReport() {
  const [preset,      setPreset]      = useState('2w');
  const [customStart, setCustomStart] = useState('');
  const [customEnd,   setCustomEnd]   = useState('');
  const [data,           setData]           = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [rawLoading,     setRawLoading]     = useState(false);
  const [error,          setError]          = useState('');

  const dates = useMemo(() => {
    if (preset !== 'custom') return presetDates(preset);
    return customStart && customEnd ? { start: customStart, end: customEnd } : null;
  }, [preset, customStart, customEnd]);

  const runReport = useCallback(async () => {
    if (!dates) return;
    setLoading(true); setError(''); setData(null);
    try {
      const { data: resp } = await api.get('/reports/nursing-checks', {
        params: { start_date: dates.start, end_date: dates.end },
      });
      setData(resp);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [dates]);

  const downloadRaw = useCallback(async () => {
    if (!dates) return;
    setRawLoading(true); setError('');
    try {
      const { data: rows } = await api.get('/reports/nursing-checks-raw', {
        params: { start_date: dates.start, end_date: dates.end },
      });
      triggerRawCSV(rows, dates.start, dates.end);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to download raw data');
    } finally {
      setRawLoading(false);
    }
  }, [dates]);

  const { summary } = data || {};

  return (
    <Box>
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: C.blue }}>
          Nurses' Daily Check Submissions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          Submission completeness by clinic and date — identify gaps and track trends over time.
        </Typography>
      </Box>

      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2.5 }}>
        <ToggleButtonGroup value={preset} exclusive size="small"
          onChange={(_, v) => v && setPreset(v)}>
          {[['2w', 'Last 2 weeks'], ['4w', 'Last 4 weeks'], ['3m', 'Last 3 months'], ['custom', 'Custom']].map(([v, lbl]) => (
            <ToggleButton key={v} value={v}
              sx={{ fontSize: '0.72rem', px: 1.5, py: 0.6, textTransform: 'none' }}>
              {lbl}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {preset === 'custom' && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
              style={{ padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: '0.78rem' }} />
            <Typography variant="caption" color="text.secondary">→</Typography>
            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              style={{ padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: '0.78rem' }} />
          </Box>
        )}

        <Button variant="contained" size="small" onClick={runReport}
          disabled={!dates || loading}
          startIcon={loading ? <CircularProgress size={13} color="inherit" /> : null}
          sx={{ bgcolor: C.blue, '&:hover': { bgcolor: '#004494' }, px: 2.5, py: 0.7,
            fontSize: '0.78rem', textTransform: 'none', fontWeight: 700 }}>
          {loading ? 'Loading…' : 'Run Report'}
        </Button>

        {data && (
          <Button variant="outlined" size="small" onClick={downloadRaw}
            disabled={!dates || rawLoading}
            startIcon={rawLoading ? <CircularProgress size={13} color="inherit" /> : <Download sx={{ fontSize: '14px !important' }} />}
            sx={{ borderColor: C.dark, color: C.dark, '&:hover': { borderColor: C.blue, color: C.blue },
              px: 2, py: 0.7, fontSize: '0.78rem', textTransform: 'none', fontWeight: 600 }}>
            {rawLoading ? 'Downloading…' : 'Download Raw Data'}
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, fontSize: '0.8rem' }}>{error}</Alert>}

      {summary && data && (
        <>
          {/* Summary cards */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
            <StatCard
              label="Overall Submission Rate"
              primary={`${summary.rate}%`}
              secondary={`${dates.start} — ${dates.end}`}
              accentColor={rateFg(summary.rate)}
            />
            <StatCard
              label="Submitted"
              primary={summary.total_submitted.toLocaleString()}
              secondary={`of ${summary.total_expected.toLocaleString()} expected`}
              accentColor={C.green}
            />
            <StatCard
              label="Missing"
              primary={summary.total_missing.toLocaleString()}
              secondary={summary.total_missing === 0 ? 'All checks received' : 'checks not received'}
              accentColor={summary.total_missing > 0 ? C.red : C.green}
            />
            <StatCard
              label="Clinics / Working Days"
              primary={String(summary.clinics)}
              secondary={`across ${summary.working_days} working day${summary.working_days !== 1 ? 's' : ''}`}
              accentColor={C.blue}
            />
          </Box>

          {/* Gap alert */}
          {summary.rate < 90 && (
            <Alert severity={summary.rate < 70 ? 'error' : 'warning'} sx={{ mb: 2, fontSize: '0.78rem' }}>
              <strong>{summary.total_missing}</strong> check{summary.total_missing !== 1 ? 's' : ''} not received
              ({(100 - summary.rate).toFixed(1)}% gap). Clinics with missing submissions are shown
              in red in the pivot table below.
            </Alert>
          )}

          {/* Pivot table */}
          <Paper elevation={0} sx={{ border: `1px solid ${C.pale}`, borderRadius: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              px: 2, py: 1 }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: C.dark }}>
                  Submission Pivot — by Clinic &amp; Date
                </Typography>
                <Typography variant="caption" sx={{ color: C.mid }}>
                  ✓ All submitted &nbsp;·&nbsp; ½ Partial &nbsp;·&nbsp; ✗ Missing &nbsp;·&nbsp; — No booking
                </Typography>
              </Box>
              <Button size="small" variant="outlined"
                startIcon={<Download sx={{ fontSize: '14px !important' }} />}
                onClick={() => exportCSV(data.pivot, data.dates)}
                sx={{ borderColor: C.blue, color: C.blue, fontSize: '0.7rem',
                  py: 0.4, px: 1.5, textTransform: 'none', flexShrink: 0 }}>
                CSV
              </Button>
            </Box>
            <Divider />
            <PivotTable dates={data.dates} pivot={data.pivot} />
          </Paper>

          {/* Run chart — only meaningful with >1 week */}
          {data.time_series.length > 1 && (
            <Paper elevation={0} sx={{ border: `1px solid ${C.pale}`, borderRadius: 2, p: 2, mb: 2 }}>
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: C.dark }}>
                  Submission Rate by Week
                </Typography>
                <Typography variant="caption" sx={{ color: C.mid }}>
                  Dashed line = 90% target
                </Typography>
              </Box>
              <RunChart timeSeries={data.time_series} />
            </Paper>
          )}

          {/* Timing analysis */}
          {summary.total_submitted > 0 && (
            <Paper elevation={0} sx={{ border: `1px solid ${C.pale}`, borderRadius: 2, p: 2, mb: 2 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: C.dark, mb: 0.5 }}>
                Submission Timing
              </Typography>
              <Typography variant="caption" sx={{ color: C.mid, display: 'block', mb: 1.5 }}>
                When were submitted checks recorded, relative to the clinic date?
              </Typography>
              <TimingBreakdown timing={data.timing} totalSubmitted={summary.total_submitted} />
            </Paper>
          )}

          {/* By-clinic summary */}
          <Paper elevation={0} sx={{ border: `1px solid ${C.pale}`, borderRadius: 2, mb: 2 }}>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: C.dark }}>
                Summary by Clinic
              </Typography>
              <Typography variant="caption" sx={{ color: C.mid }}>
                Sorted by submission rate — lowest first
              </Typography>
            </Box>
            <Divider />
            <ByClinicTable pivot={data.pivot} />
          </Paper>

          <Alert severity="info" icon={<InfoOutlined sx={{ fontSize: 18 }} />}
            sx={{ fontSize: '0.75rem', '& .MuiAlert-message': { pt: '2px' } }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', mb: 0.25 }}>Note</Typography>
            <Typography sx={{ fontSize: '0.72rem' }}>
              Only confirmed bookings are included. A check is "expected" for every confirmed booking in
              the selected period. Submission timing is relative to the clinic date (midnight). Weekends
              are excluded from working day counts.
            </Typography>
          </Alert>
        </>
      )}

      {!data && !loading && (
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', height: 180, color: C.mid, gap: 1,
          border: `1.5px dashed ${C.pale}`, borderRadius: 2, mt: 1,
        }}>
          <Typography variant="body2">Select a date range and click Run Report</Typography>
          {dates && (
            <Typography variant="caption" sx={{ color: C.pale }}>{dates.start} → {dates.end}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
